import { formatPhoneNumberIntl } from "react-phone-number-input";

// One rule for naming a contact, shared by the customer list, the inbox and
// the customer profile:
//   1. A real name (set by the owner, imported, or the channel's profile name).
//   2. Otherwise their main contact detail: the channel they use most
//      (preferred channel), else the one they first reached you on.
//      WhatsApp → phone number, Instagram/Telegram → @username,
//      email → the address, Telegram without a username → "Telegram · id".
//   3. Otherwise "Unnamed". Never "Unknown customer" or "<Channel> contact".

export interface ContactIdentifier {
  channel_type: string;
  identifier_value: string;
  handle?: string | null;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
}

const CHANNEL_TYPE_BY_CHANNEL: Record<string, string> = {
  whatsapp: "whatsapp_e164",
  instagram: "instagram_psid",
  telegram: "telegram_chat_id",
  line: "line_user_id",
  facebook: "facebook_psid",
  facebook_messenger: "facebook_psid",
  messenger: "facebook_psid",
  tiktok: "tiktok",
  email: "email",
};

const CHANNEL_NAMES: Record<string, string> = {
  whatsapp_e164: "WhatsApp",
  instagram_psid: "Instagram",
  telegram_chat_id: "Telegram",
  line_user_id: "LINE",
  facebook_psid: "Messenger",
  tiktok: "TikTok",
  email: "Email",
};

/** Channel types a person can be reached on, in the order the profile lists them. */
export const CONTACT_CHANNEL_TYPES = Object.keys(CHANNEL_NAMES);

export function channelTypeForChannel(channel: string | null | undefined): string | null {
  if (!channel) return null;
  const key = channel.trim().toLowerCase();
  return CHANNEL_TYPE_BY_CHANNEL[key] ?? (CHANNEL_NAMES[key] ? key : null);
}

export function channelName(channelType: string): string {
  return CHANNEL_NAMES[channelType] ?? channelType.replace(/_/g, " ");
}

export function formatPhoneForDisplay(raw: string | null | undefined): string {
  const value = (raw ?? "").replace(/^whatsapp:/i, "").trim();
  if (!value) return "";
  if (!value.startsWith("+")) return value;
  return formatPhoneNumberIntl(`+${value.replace(/\D/g, "")}`) || value;
}

const cleanHandle = (handle: string | null | undefined) =>
  handle?.trim().replace(/^@+/, "").trim() || null;

/** The readable form of one identity, or null when it is only a machine id. */
export function identifierLabel(identifier: ContactIdentifier): string | null {
  const handle = cleanHandle(identifier.handle);
  switch (identifier.channel_type) {
    case "whatsapp_e164":
      return formatPhoneForDisplay(identifier.identifier_value) || null;
    case "instagram_psid":
    case "tiktok":
      return handle ? `@${handle}` : null;
    case "telegram_chat_id":
      return handle ? `@${handle}` : `Telegram · ${identifier.identifier_value}`;
    case "email":
      return identifier.identifier_value.trim() || null;
    default:
      // LINE and Messenger ids aren't readable; their profile names arrive
      // as the contact's name instead.
      return null;
  }
}

const time = (value: string | null | undefined, fallback: number) => {
  const parsed = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
};

/** The contact detail that stands in for, or sits next to, a name. */
export function primaryIdentity(args: {
  identifiers: ContactIdentifier[];
  preferredChannel?: string | null;
  primaryPhone?: string | null;
  primaryEmail?: string | null;
}): string | null {
  const readable = args.identifiers
    .map((identifier) => ({ identifier, label: identifierLabel(identifier) }))
    .filter((entry): entry is { identifier: ContactIdentifier; label: string } => !!entry.label);

  const preferredType = channelTypeForChannel(args.preferredChannel);
  if (preferredType) {
    const onPreferred = readable
      .filter((entry) => entry.identifier.channel_type === preferredType)
      .sort((a, b) => time(b.identifier.last_seen_at, 0) - time(a.identifier.last_seen_at, 0));
    if (onPreferred[0]) return onPreferred[0].label;
  }

  const firstSeen = [...readable].sort((a, b) =>
    time(a.identifier.first_seen_at, Number.MAX_SAFE_INTEGER) -
    time(b.identifier.first_seen_at, Number.MAX_SAFE_INTEGER)
  )[0];
  if (firstSeen) return firstSeen.label;

  if (args.primaryPhone?.trim()) return formatPhoneForDisplay(args.primaryPhone);
  if (args.primaryEmail?.trim()) return args.primaryEmail.trim();
  return null;
}

/** The identity of one conversation, from the channel it's on. */
export function conversationIdentity(args: {
  channel: string | null | undefined;
  channelUserId: string | null | undefined;
  handle?: string | null;
}): string | null {
  const channelType = channelTypeForChannel(args.channel ?? "whatsapp");
  if (!channelType || !args.channelUserId) return null;
  return identifierLabel({
    channel_type: channelType,
    identifier_value: args.channelUserId,
    handle: args.handle,
  });
}

export function contactDisplayName(
  name: string | null | undefined,
  identity: string | null | undefined,
): string {
  return name?.trim() || identity?.trim() || "Unnamed";
}

/** A bare channel id ("Telegram · 123") stands in for a missing name, but is
 *  noise next to a real one. */
export const isRawIdLabel = (label: string) => label.startsWith("Telegram ·");

/** "Name (detail)" when both exist and differ; otherwise whichever exists. */
export function nameWithIdentity(
  name: string | null | undefined,
  identity: string | null | undefined,
): string {
  const cleanName = name?.trim();
  const cleanIdentity = identity?.trim();
  if (cleanName && cleanIdentity && cleanName !== cleanIdentity && !isRawIdLabel(cleanIdentity)) {
    return `${cleanName} (${cleanIdentity})`;
  }
  return contactDisplayName(cleanName, cleanIdentity);
}
